import { cn } from '@/lib/cn'
import type { ScoreRow } from './scoreRows'
import { formatScore } from '@/lib/format'
import { IconButton } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Overlay'
import { Info } from 'lucide-react'
import { useState } from 'react'

/**
 * Score details, behind a small info affordance next to the score.
 *
 * The overlay keeps the breakdown out of the card while preserving every
 * component the engine returned.
 */
export function ScoreBreakdown({
  rows,
  total,
  totalLabel,
}: {
  rows: ScoreRow[]
  total: number
  totalLabel: string
}) {
  const [open, setOpen] = useState(false)
  // Bars are scaled against the largest single contribution so the widest bar
  // always fills the track, rather than against the total, which would leave
  // every bar looking small.
  const peak = Math.max(
    ...rows.flatMap((row) => [Math.abs(row.utility), row.exploration, Math.abs(row.penalty ?? 0)]),
    0.0001,
  )

  return (
    <>
      <IconButton
        label={`Why this scores ${formatScore(total)}`}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="mt-0.5 size-6"
      >
        <Info size={13} aria-hidden />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Why this scores ${formatScore(total)}`}
        description={totalLabel}
        size="sm"
      >
        <ul className="space-y-2.5">
          {rows.map((row) => (
            <li key={row.label}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-xs text-ink-soft">{row.label}</span>
                <span className="tabular shrink-0 text-xs font-medium text-ink">
                  {formatScore(row.total)}
                </span>
              </div>
              <div className="mt-1 flex h-1.5 gap-0.5 overflow-hidden rounded-full">
                <ContributionBar
                  value={row.utility}
                  peak={peak}
                  className="bg-protein"
                  label="Utility"
                />
                <ContributionBar
                  value={row.exploration}
                  peak={peak}
                  className="bg-positive"
                  label="Exploration"
                />
                <ContributionBar
                  value={-(row.penalty ?? 0)}
                  peak={peak}
                  className="bg-critical"
                  label="Penalty"
                />
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line pt-2.5">
          {(
            [
              ['bg-protein', 'Utility'],
              ['bg-positive', 'Exploration'],
              ['bg-critical', 'Penalty'],
            ] as const
          ).map(([colour, label]) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-micro text-ink-faint">
              <span aria-hidden className={`size-2 rounded-full ${colour}`} />
              {label}
            </span>
          ))}
        </div>

        <div className="-mx-1 mt-3 overflow-x-auto px-1">
          <table className="w-full min-w-[24rem] text-xs">
            <caption className="sr-only">
              Score components per meal, with a total of {formatScore(total)}
            </caption>
            <thead>
              <tr className="text-left text-micro text-ink-faint">
                <th scope="col" className="py-1 pr-2 font-medium">
                  Component
                </th>
                <th scope="col" className="py-1 pr-2 text-right font-medium">
                  Utility
                </th>
                <th scope="col" className="py-1 pr-2 text-right font-medium">
                  Exploration
                </th>
                <th scope="col" className="py-1 pr-2 text-right font-medium">
                  Penalty
                </th>
                <th scope="col" className="py-1 text-right font-medium">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="tabular">
              {rows.map((row) => (
                <tr key={row.label} className="border-t border-line">
                  <th scope="row" className="py-1 pr-2 text-left font-normal text-ink-soft">
                    {row.label}
                  </th>
                  <td className="py-1 pr-2 text-right text-ink-soft">{formatScore(row.utility)}</td>
                  <td className="py-1 pr-2 text-right text-positive">
                    +{formatScore(row.exploration)}
                  </td>
                  <td className="py-1 pr-2 text-right text-critical">
                    {row.penalty ? `-${formatScore(row.penalty)}` : '—'}
                  </td>
                  <td className="py-1 text-right font-medium text-ink">
                    {formatScore(row.total)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-line-strong">
                <th scope="row" className="py-1.5 pr-2 text-left font-medium text-ink">
                  {totalLabel}
                </th>
                <td colSpan={3} />
                <td className="tabular py-1.5 text-right text-sm font-semibold text-ink">
                  {formatScore(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-2 text-micro text-pretty text-ink-faint">
          Utility is rating minus an effort penalty. Exploration grows for recipes you
          have not prepared in a while and fades as they age back towards your average.
        </p>
      </Dialog>
    </>
  )
}

/**
 * One contribution, as a filled portion of its own track. The track holds the
 * layout and the fill holds the measurement — setting the width on the flex
 * child itself would be overridden by its flex-basis and every bar would come
 * out the same length.
 *
 * Penalties get the critical colour in the third track, so a deduction never
 * reads as a bonus.
 */
function ContributionBar({
  value,
  peak,
  className,
  label,
}: {
  value: number
  peak: number
  className: string
  label: string
}) {
  const magnitude = Math.min(1, Math.abs(value) / peak)
  return (
    <span aria-hidden className="flex-1 overflow-hidden rounded-full bg-line/70">
      <span
        title={`${label} ${formatScore(value)}`}
        className={cn('block h-full rounded-full', className)}
        style={{ width: `${Math.max(magnitude * 100, magnitude > 0 ? 4 : 0)}%` }}
      />
    </span>
  )
}
